import { generateClient } from 'aws-amplify/api'
import { listTeamMembers as listTeamMembersQuery } from '@/graphql/queries'
import {
  createTeamMember as createTeamMemberMutation,
  bulkCreateTeamMembers as bulkCreateTeamMembersMutation,
  editTeamMember as editTeamMemberMutation,
  deleteTeamMember as deleteTeamMemberMutation,
} from '@/graphql/mutations'
import { parseAwsJson } from '@/utils/graphql'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'

const client = generateClient()

/**
 * The team directory: people an on-call action may contact — sending them a
 * document, or scheduling a meeting with them. They are NOT portal users and
 * cannot sign in; this is a contact list, not an account list.
 */
async function runTeamField(document, field, input) {
  try {
    const variables = input == null ? undefined : { input }
    const { data, errors } = await client.graphql({
      query: document,
      ...(variables ? { variables } : {}),
    })
    if (errors?.length) throw new Error(errors[0].message)
    return parseAwsJson(data?.[field])
  } catch (error) {
    return { status: 'ERROR', message: getApiMessage(error, 'Request failed') }
  }
}

export async function fetchTeamMembers() {
  const response = await runTeamField(listTeamMembersQuery, 'listTeamMembers', null)
  if (!isApiSuccess(response)) return response
  const items = response?.data?.items ?? []
  return {
    ...response,
    data: {
      ...(response.data ?? {}),
      items: items.map((item) => ({
        id: String(item.team_member_id ?? ''),
        name: String(item.name ?? ''),
        email: String(item.email ?? ''),
        contactNumber: String(item.contact_number ?? ''),
        linkedToAccount: Boolean(item.linked_to_account),
      })),
    },
  }
}

export function createTeamMember(input = {}) {
  return runTeamField(createTeamMemberMutation, 'createTeamMember', {
    name: String(input.name ?? '').trim(),
    email: String(input.email ?? '').trim(),
    contact_number: String(input.contactNumber ?? input.contact_number ?? '').trim(),
  })
}

export function bulkCreateTeamMembers(members = []) {
  return runTeamField(bulkCreateTeamMembersMutation, 'bulkCreateTeamMembers', {
    members: members.map((row) => ({
      name: String(row.name ?? '').trim(),
      email: String(row.email ?? '').trim(),
      contact_number: String(row.contact_number ?? row.contactNumber ?? '').trim(),
    })),
  })
}

export function editTeamMember(input = {}) {
  const payload = { team_member_id: String(input.id ?? input.team_member_id ?? '').trim() }
  // Only send what changed: an omitted field is left alone by the backend,
  // so an edit of just the phone number cannot blank out the email.
  if (input.name !== undefined) payload.name = String(input.name).trim()
  if (input.email !== undefined) payload.email = String(input.email).trim()
  if (input.contactNumber !== undefined) {
    payload.contact_number = String(input.contactNumber).trim()
  }
  return runTeamField(editTeamMemberMutation, 'editTeamMember', payload)
}

export function deleteTeamMember(input = {}) {
  return runTeamField(deleteTeamMemberMutation, 'deleteTeamMember', {
    team_member_id: String(input.id ?? input.team_member_id ?? '').trim(),
  })
}
